import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { StandardSideDrawer } from "../../../../../shared/components/drawers/Drawers"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"
import { getHostOrganizer } from "../../home/components/meetigs/helpers"

import { formatTimeWindow, getRoomBookingItems } from "../meetingRoomsLib"
import { MeetingRoomBookingState, MeetingRoomNowStatus } from "../meetingRoomsTypes"
import type { MeetingRoomBookingItem, MeetingRoomBusyInterval, MeetingRoomRow } from "../meetingRoomsTypes"
import { LIVE_PULSE } from "./helpers"
import { MeetingRoomBookingParticipants } from "./MeetingRoomBookingParticipants"
import { MeetingRoomBookingStateChip } from "./MeetingRoomBookingStateChip"
import { MeetingRoomDayTimeline } from "./MeetingRoomDayTimeline"
import { MeetingRoomDetailRow } from "./MeetingRoomDetailRow"
import { MeetingRoomDrawerSection } from "./MeetingRoomDrawerSection"
import { MeetingRoomDrawerSubheading } from "./MeetingRoomDrawerSubheading"

const useStyles = makeStyles()((theme) => ({
    statusDot: {
        width: ".5rem",
        height: ".5rem",
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: theme.palette.tailwind.teal[500],
    },
    statusDotBusy: {
        backgroundColor: theme.palette.tailwind.amber[500],
        ...LIVE_PULSE,
    },
    content: {
        flex: 1,
        minHeight: 0,
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        paddingBottom: "1rem",
        "& > *": {
            flexShrink: 0,
        },
    },
    footer: {
        flexShrink: 0,
        paddingTop: "1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
}))

type Props = Readonly<{
    room?: MeetingRoomRow
    selectedOrderId?: number
    nowMillis: number
    isToday: boolean
    hourWindow: MeetingRoomBusyInterval
    hourLabel: string
    dayStartMillis: number
    dayEndMillis: number
    onSelectBooking: (item: MeetingRoomBookingItem) => void
    onReceiptOpen: (orderId: number) => void
    onClose: () => void
}>

export function MeetingRoomDrawer({
    room,
    selectedOrderId,
    nowMillis,
    isToday,
    hourWindow,
    hourLabel,
    dayStartMillis,
    dayEndMillis,
    onSelectBooking,
    onReceiptOpen,
    onClose,
}: Props) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    const items = room ? getRoomBookingItems(room, nowMillis) : []
    const currentItem = items.find((item) => item.state === MeetingRoomBookingState.IN_PROGRESS)
    const currentBusyWindow =
        currentItem?.timeRange ??
        room?.unbookedBusyIntervals.find(
            ({ startMillis, endMillis }) => startMillis <= nowMillis && endMillis > nowMillis,
        )
    const nextItem = items.find((item) => item.state === MeetingRoomBookingState.UPCOMING)
    const isInHour = (timeRange: MeetingRoomBusyInterval) =>
        timeRange.startMillis < hourWindow.endMillis && timeRange.endMillis > hourWindow.startMillis
    const hourItem = items.find(({ timeRange }) => isInHour(timeRange))
    const isInUseInHour = Boolean(hourItem) || Boolean(room?.unbookedBusyIntervals.some(isInHour))
    const nextFromHourItem = items.find(({ timeRange }) => timeRange.startMillis >= hourWindow.startMillis)
    const selectedItem = items.find((item) => item.booking.orderId === selectedOrderId)
    const host = selectedItem ? getHostOrganizer([...selectedItem.booking.organizers]) : null
    const formatTime = (millis: number) => dateTimeFactory.formatTime(dateTimeFactory.fromMillis(millis))

    const renderStatus = () => {
        if (currentBusyWindow && nextItem) {
            return (
                <LocalizedStrict
                    id="service-center-meeting-rooms-drawer-status-occupied-next"
                    vars={{
                        time: formatTime(currentBusyWindow.endMillis),
                        nextTime: formatTime(nextItem.timeRange.startMillis),
                        meeting: nextItem.booking.meetingName ?? "",
                    }}
                >
                    <span>Occupied</span>
                </LocalizedStrict>
            )
        }
        if (currentBusyWindow) {
            return (
                <LocalizedStrict
                    id="service-center-meeting-rooms-drawer-status-occupied"
                    vars={{ time: formatTime(currentBusyWindow.endMillis) }}
                >
                    <span>Occupied</span>
                </LocalizedStrict>
            )
        }
        if (nextItem) {
            return (
                <LocalizedStrict
                    id="service-center-meeting-rooms-drawer-status-free-next"
                    vars={{
                        nextTime: formatTime(nextItem.timeRange.startMillis),
                        meeting: nextItem.booking.meetingName ?? "",
                    }}
                >
                    <span>Available now</span>
                </LocalizedStrict>
            )
        }
        return (
            <LocalizedStrict id="service-center-meeting-rooms-drawer-status-free-rest-of-day">
                <span>Available for the rest of the day</span>
            </LocalizedStrict>
        )
    }

    const activityStatus = (
        <LocalizedEnum
            base="service-center-meeting-rooms-drawer-activity-status"
            enumValue={isInUseInHour ? MeetingRoomNowStatus.IN_USE : MeetingRoomNowStatus.FREE}
        >
            <span>{isInUseInHour ? "In use" : "Available"}</span>
        </LocalizedEnum>
    )

    const nextBooking = nextFromHourItem ? (
        <LocalizedStrict
            id="service-center-meeting-rooms-drawer-next-booking-value"
            vars={{
                time: formatTime(nextFromHourItem.timeRange.startMillis),
                meeting: nextFromHourItem.booking.meetingName ?? "",
            }}
        >
            <span>{nextFromHourItem.booking.meetingName}</span>
        </LocalizedStrict>
    ) : (
        <LocalizedStrict id="service-center-meeting-rooms-drawer-next-booking-none">
            <span>None today</span>
        </LocalizedStrict>
    )

    const renderSeats = (seats: number) => (
        <LocalizedStrict id="service-center-meeting-rooms-drawer-capacity-value" vars={{ seats }}>
            <span>{seats}</span>
        </LocalizedStrict>
    )

    return (
        <StandardSideDrawer
            name="meeting-room"
            open={room !== undefined}
            onClose={onClose}
            title={room?.name}
            subtitle={
                room &&
                isToday && (
                    <Stack direction="row" alignItems="center" spacing=".5rem" component="span">
                        <span className={cx(classes.statusDot, currentItem && classes.statusDotBusy)} />
                        {renderStatus()}
                    </Stack>
                )
            }
        >
            {room && (
                <Stack className={classes.content}>
                    <Stack spacing=".875rem" className={classes.body}>
                        <MeetingRoomDrawerSection
                            isDefaultOpen={false}
                            title={
                                <LocalizedStrict id="service-center-meeting-rooms-drawer-room-section">
                                    <span>About the room</span>
                                </LocalizedStrict>
                            }
                            collapsedSummary={
                                <>
                                    {room.floorName}
                                    {room.seats !== undefined && <> · {renderSeats(room.seats)}</>}
                                </>
                            }
                        >
                            <MeetingRoomDetailRow
                                label={
                                    <LocalizedStrict id="service-center-meeting-rooms-drawer-floor-label">
                                        <span>Floor</span>
                                    </LocalizedStrict>
                                }
                                value={room.floorName}
                            />
                            {room.seats !== undefined && (
                                <MeetingRoomDetailRow
                                    label={
                                        <LocalizedStrict id="service-center-meeting-rooms-drawer-capacity-label">
                                            <span>Capacity</span>
                                        </LocalizedStrict>
                                    }
                                    value={renderSeats(room.seats)}
                                />
                            )}
                            {room.equipment.length > 0 && (
                                <MeetingRoomDetailRow
                                    label={
                                        <LocalizedStrict id="service-center-meeting-rooms-drawer-equipment-label">
                                            <span>Equipment</span>
                                        </LocalizedStrict>
                                    }
                                    value={room.equipment.join(" · ")}
                                />
                            )}
                        </MeetingRoomDrawerSection>

                        <MeetingRoomDayTimeline
                            items={items}
                            unbookedBusyIntervals={room.unbookedBusyIntervals}
                            selectedOrderId={selectedItem?.booking.orderId}
                            nowMillis={nowMillis}
                            dayStartMillis={dayStartMillis}
                            dayEndMillis={dayEndMillis}
                            onSelect={onSelectBooking}
                        />

                        {selectedItem ? (
                            <MeetingRoomDrawerSection
                                key={selectedItem.booking.orderId}
                                title={selectedItem.booking.meetingName}
                                summary={formatTimeWindow(dateTimeFactory, selectedItem.timeRange)}
                                headerAside={<MeetingRoomBookingStateChip state={selectedItem.state} />}
                            >
                                <MeetingRoomDetailRow
                                    label={
                                        <LocalizedStrict id="service-center-meeting-rooms-drawer-booking-id-label">
                                            <span>Booking ID</span>
                                        </LocalizedStrict>
                                    }
                                    value={`#${selectedItem.booking.orderId}`}
                                />
                                {selectedItem.booking.participants !== undefined && (
                                    <MeetingRoomDetailRow
                                        label={
                                            <LocalizedStrict id="service-center-meeting-rooms-drawer-participants-label">
                                                <span>Participants</span>
                                            </LocalizedStrict>
                                        }
                                        value={
                                            <MeetingRoomBookingParticipants
                                                participants={selectedItem.booking.participants}
                                                seats={room.seats}
                                            />
                                        }
                                    />
                                )}
                                {selectedItem.booking.tableSetupName && (
                                    <MeetingRoomDetailRow
                                        label={
                                            <LocalizedStrict id="service-center-meeting-rooms-drawer-table-setup-label">
                                                <span>Table setup</span>
                                            </LocalizedStrict>
                                        }
                                        value={selectedItem.booking.tableSetupName}
                                    />
                                )}
                                {selectedItem.booking.hasCatering !== undefined && (
                                    <MeetingRoomDetailRow
                                        label={
                                            <LocalizedStrict id="service-center-meeting-rooms-drawer-catering-label">
                                                <span>Catering</span>
                                            </LocalizedStrict>
                                        }
                                        value={
                                            selectedItem.booking.hasCatering ? (
                                                <LocalizedStrict id="service-center-meeting-rooms-drawer-catering-ordered">
                                                    <span>Ordered</span>
                                                </LocalizedStrict>
                                            ) : (
                                                <LocalizedStrict id="service-center-meeting-rooms-drawer-catering-not-ordered">
                                                    <span>Not ordered</span>
                                                </LocalizedStrict>
                                            )
                                        }
                                    />
                                )}
                                {host && (
                                    <>
                                        <LocalizedStrict id="service-center-meeting-rooms-drawer-owner-subheading">
                                            <MeetingRoomDrawerSubheading>Meeting owner</MeetingRoomDrawerSubheading>
                                        </LocalizedStrict>
                                        <MeetingRoomDetailRow
                                            hasBorder={false}
                                            label={
                                                <LocalizedStrict id="service-center-meeting-rooms-drawer-name-label">
                                                    <span>Name</span>
                                                </LocalizedStrict>
                                            }
                                            value={host.name}
                                        />
                                        {host.department?.name && (
                                            <MeetingRoomDetailRow
                                                label={
                                                    <LocalizedStrict id="service-center-meeting-rooms-drawer-department-label">
                                                        <span>Department</span>
                                                    </LocalizedStrict>
                                                }
                                                value={host.department.name}
                                            />
                                        )}
                                        {(host.phone || host.mobilePhone || host.businessPhone) && (
                                            <MeetingRoomDetailRow
                                                label={
                                                    <LocalizedStrict id="service-center-meeting-rooms-drawer-phone-label">
                                                        <span>Phone</span>
                                                    </LocalizedStrict>
                                                }
                                                value={host.phone || host.mobilePhone || host.businessPhone}
                                            />
                                        )}
                                        {host.email && (
                                            <MeetingRoomDetailRow
                                                label={
                                                    <LocalizedStrict id="service-center-meeting-rooms-drawer-email-label">
                                                        <span>E-mail</span>
                                                    </LocalizedStrict>
                                                }
                                                value={host.email}
                                            />
                                        )}
                                    </>
                                )}
                            </MeetingRoomDrawerSection>
                        ) : (
                            <MeetingRoomDrawerSection
                                title={
                                    <LocalizedStrict id="service-center-meeting-rooms-drawer-activity-section">
                                        <span>Activity</span>
                                    </LocalizedStrict>
                                }
                                collapsedSummary={
                                    <>
                                        {activityStatus} · {nextBooking}
                                    </>
                                }
                            >
                                <MeetingRoomDetailRow
                                    label={
                                        <LocalizedStrict
                                            id="service-center-meeting-rooms-drawer-activity-status-label"
                                            vars={{ time: hourLabel }}
                                        >
                                            <span>Status</span>
                                        </LocalizedStrict>
                                    }
                                    value={activityStatus}
                                />
                                <MeetingRoomDetailRow
                                    label={
                                        <LocalizedStrict id="service-center-meeting-rooms-drawer-next-booking-label">
                                            <span>Next booking</span>
                                        </LocalizedStrict>
                                    }
                                    value={nextBooking}
                                />
                                <MeetingRoomDetailRow
                                    label={
                                        <LocalizedStrict id="service-center-meeting-rooms-drawer-bookings-today-label">
                                            <span>Bookings today</span>
                                        </LocalizedStrict>
                                    }
                                    value={
                                        <LocalizedStrict
                                            id="service-center-meeting-rooms-drawer-bookings-today-value"
                                            vars={{ count: items.length }}
                                        >
                                            <span>{items.length} bookings</span>
                                        </LocalizedStrict>
                                    }
                                />
                            </MeetingRoomDrawerSection>
                        )}
                    </Stack>
                    {selectedItem && (
                        <Stack className={classes.footer}>
                            <LocalizedStrict id="service-center-meeting-rooms-drawer-receipt-button">
                                <StandardButton
                                    color="black"
                                    fullWidth
                                    onClick={() => onReceiptOpen(selectedItem.booking.orderId)}
                                >
                                    View receipt
                                </StandardButton>
                            </LocalizedStrict>
                        </Stack>
                    )}
                </Stack>
            )}
        </StandardSideDrawer>
    )
}
